import { GetServerSideProps } from 'next'
import api from 'services/api'
import Layout from 'components/layout'
import NumberPagination from 'components/number-pagination'
import Comment from 'models/comment'
import routes from 'routes'
import { NextSeo } from 'next-seo'
import { CANONICAL_PREFIX } from 'config'
import Header from 'components/header'
import Keywords from 'components/keywords'
import BookDetails from 'models/book-details'
import Avatar from 'components/avatar'
import { formatUtcString } from 'utils/date'
import { CommonJsonLd } from 'components/json-ld'

const LIMIT = 100

const CommentPage = ({
  id,
  bookInfo,
  comments,
  current,
  total,
  path,
}: {
  id: string
  bookInfo: BookDetails
  comments: Comment
  current: number
  total: number
  path: string
}): JSX.Element => {
  const { book } = bookInfo
  const linkGeneration = (num: number) =>
    routes.comment.list({ id: id, pages: num })
  return (
    <Layout>
      <Keywords keywords={[`${book.title} Comments`]} />
      <NextSeo
        title={`${book.title} - AnyStories`}
        description={`${book.title} Comments`}
        canonical={`${CANONICAL_PREFIX}${path}`}
      />
      <CommonJsonLd />
      <Header
        title={`${book.title} Comments`}
        description={''}
        breadcrumbItems={[
          {
            name: `${book.title}`,
            link: routes.story.details({ id, title: `${book.title}` }),
          },
          {
            name: 'Comments',
          },
        ]}
      />

      <div className="p-3">
        <ul>
          {comments.list.map((item) => {
            return item.user ? (
              <li className="flex items-start mb-6" key={item.id}>
                <div className="flex-none w-12">
                  <Avatar src={item.user.avatar} className="w-8 h-8 mr-2" />
                </div>
                <div className="flex flex-col">
                  <h2 className="font-bold mb-1">{item.user.name}</h2>
                  <p className="text-sm mb-1">{item.content}</p>
                  <div className="text-xs text-gray-400">
                    {formatUtcString(item.ctime)}
                  </div>
                </div>
              </li>
            ) : null
          })}
        </ul>
      </div>
      {total > 1 && (
        <>
          <hr className="m-4 border-gray-300" />
          <div className="mx-2 my-4">
            <NumberPagination
              current={current}
              total={total}
              linkGeneration={linkGeneration}
            />
          </div>
        </>
      )}
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const id = context.params.id as string
  const pages = +(context.params.pages as string) || 1

  if (!pages || pages < 1) return { notFound: true }

  const [bookInfo, comments] = await Promise.all([
    api.book.getBookInfo(id),
    api.comment.getComments({
      id,
      offset: pages,
      limit: LIMIT,
    }),
  ])

  if (!bookInfo) {
    return {
      notFound: true,
    }
  }

  const path = routes.comment.list({ id, pages })

  return {
    props: {
      id,
      bookInfo,
      comments,
      current: pages,
      total: Math.ceil(comments.total / LIMIT),
      path,
    },
  }
}

export default CommentPage
